import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";

const emptyTerms = {
  basePlanId: "",
  amount: "",
  recurrence: "monthly",
  users: 0,
  connections: 0,
  queues: 0,
  aiTokensLimit: 0,
  dueDate: "",
};

const CompanyBillingContractPanel = ({ companyId, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [terms, setTerms] = useState(emptyTerms);
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [monthlyPolicy, setMonthlyPolicy] = useState("FUTURE_ONLY");
  const [idempotencyKey, setIdempotencyKey] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [{ data: contractData }, { data: planData }] = await Promise.all([
        api.get(`/companies/${companyId}/billing-contract`),
        api.get("/plans/all"),
      ]);
      setData(contractData);
      const availablePlans = Array.isArray(planData) ? planData : [];
      if (
        contractData.plan &&
        !availablePlans.some((plan) => plan.id === contractData.plan.id)
      ) {
        availablePlans.push(contractData.plan);
      }
      setPlans(availablePlans);
      const effective = contractData.effectiveTerms || {};
      const benefits = effective.benefitsSnapshot || {};
      setTerms({
        basePlanId: effective.planVersionId || "",
        amount: Number.isFinite(Number(effective.contractedValueCents))
          ? (Number(effective.contractedValueCents) / 100).toFixed(2)
          : "",
        recurrence: effective.recurrence || "monthly",
        users: benefits.users || 0,
        connections: benefits.connections || 0,
        queues: benefits.queues || 0,
        aiTokensLimit: benefits.aiTokensLimit || 0,
        dueDate: effective.dueDate || "",
      });
      setPreview(null);
      setIdempotencyKey("");
      setReason("");
    } catch (error) {
      toast.error("Não foi possível carregar o contrato financeiro.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!companyId) return null;

  const change = (field) => (event) => {
    setTerms((current) => ({ ...current, [field]: event.target.value }));
    setPreview(null);
    setIdempotencyKey("");
  };

  const changes = () => ({
    basePlanId: terms.basePlanId ? Number(terms.basePlanId) : undefined,
    amount: terms.amount,
    recurrence: terms.recurrence,
    users: Number(terms.users),
    connections: Number(terms.connections),
    queues: Number(terms.queues),
    aiTokensLimit: Number(terms.aiTokensLimit),
    dueDate: terms.dueDate || null,
  });

  const featureEnabled = data?.featureEnabled === true;
  const requiresInitialization =
    data?.requiresContractInitialization === true;
  const monthlyFeatureEnabled =
    data?.monthlyContractChangeEnabled === true;
  const special = data?.plan?.scope === "COMPANY_SPECIFIC";
  const monthlyFlow =
    terms.recurrence === "monthly" &&
    data?.hasMonthlyProviderSubscription === true;

  const handleInitialize = async () => {
    setLoading(true);
    try {
      await api.post(`/companies/${companyId}/billing-contract/initialize`);
      toast.success(
        "Plano antigo preparado para edição. Nenhuma cobrança foi enviada ao Asaas."
      );
      await load();
      if (onApplied) onApplied();
    } catch (error) {
      toast.error("Não foi possível preparar este plano antigo para edição.");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      const { data: response } = monthlyFlow
        ? await api.post(
            `/companies/${companyId}/monthly-contract-change/preview`,
            { changes: changes(), policy: monthlyPolicy }
          )
        : await api.post(`/companies/${companyId}/billing-contract/preview`, {
            changes: changes(),
          });
      setPreview(response);
      setIdempotencyKey(
        monthlyFlow || response?.transition?.transitionRequired
          ? `${monthlyFlow ? "monthly" : "transition"}-${companyId}-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2, 12)}`
          : ""
      );
    } catch (error) {
      toast.error("Não foi possível gerar a prévia segura da alteração.");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!preview || reason.trim().length < 5) {
      toast.error("Informe um motivo com pelo menos 5 caracteres.");
      return;
    }
    setLoading(true);
    try {
      const isMonthlyProviderChange = Boolean(preview?.providerPreview);
      const isScheduledTransition =
        preview?.transition?.transitionRequired === true &&
        preview?.transition?.strategy !== "USE_MONTHLY_CHANGE_R3";
      if (isMonthlyProviderChange) {
        await api.post(`/companies/${companyId}/monthly-contract-change/apply`, {
          changes: changes(),
          policy: monthlyPolicy,
          previewDigest: preview.previewDigest,
          idempotencyKey,
          reason: reason.trim(),
        });
      } else if (isScheduledTransition) {
        await api.post(`/companies/${companyId}/plan-transition/schedule`, {
          changes: changes(),
          expectedLockVersion: preview.expectedLockVersion,
          idempotencyKey,
          reason: reason.trim(),
        });
      } else {
        await api.put(`/companies/${companyId}/billing-contract`, {
          changes: changes(),
          expectedLockVersion: preview.expectedLockVersion,
          reason: reason.trim(),
        });
      }
      toast.success(
        isMonthlyProviderChange
          ? "Alteração mensal aplicada sem trocar o ID da assinatura."
          : isScheduledTransition
          ? "Transição agendada para o fim do período atual."
          : "Condição especial criada somente para esta empresa."
      );
      await load();
      if (onApplied) onApplied();
    } catch (error) {
      toast.error(
        "Não foi possível concluir. Consulte o pedido de reconciliação antes de tentar outra alteração."
      );
    } finally {
      setLoading(false);
    }
  };

  const privatePreview = preview?.privateContractPreview || preview;

  return (
    <Paper variant="outlined" style={{ padding: 16, marginTop: 12 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Typography variant="h6">Contrato financeiro individual</Typography>
          <Chip
            size="small"
            color={special ? "secondary" : "default"}
            label={data?.planLabel || "Carregando contrato..."}
            style={{ marginRight: 8 }}
          />
          {data?.pendingTransition && (
            <Chip
              size="small"
              color="primary"
              label={`Transição agendada · ${data.pendingTransition.effectiveDate || "fim do período"}`}
              style={{ marginLeft: 8 }}
            />
          )}
          <Chip
            size="small"
            label={`Estado: ${data?.effectiveTerms?.status || "-"}`}
          />
        </Grid>

        {!featureEnabled && (
          <Grid item xs={12}>
            <Typography color="textSecondary">
              Publicado com BILLING_CONTRACTS_ENABLED desligada. É possível
              consultar a estrutura, mas a aplicação permanece bloqueada.
            </Typography>
          </Grid>
        )}
        {requiresInitialization && (
          <Grid item xs={12}>
            <Paper variant="outlined" style={{ padding: 12 }}>
              <Typography>
                Este é um plano antigo, anterior ao controle financeiro atual.
                Os dados abaixo podem ser consultados normalmente.
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                Para editar somente esta empresa, prepare o contrato atual. A
                preparação apenas registra a versão e a auditoria; não cria,
                cancela nem altera cobranças no Asaas.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                disabled={loading}
                onClick={handleInitialize}
              >
                Preparar plano antigo para edição
              </Button>
            </Paper>
          </Grid>
        )}
        {monthlyFlow && !monthlyFeatureEnabled && (
          <Grid item xs={12}>
            <Typography color="textSecondary">
              A troca mensal segura está publicada com
              BILLING_CONTRACT_CHANGE_ENABLED desligada. O fluxo antigo de
              cancelar a assinatura foi bloqueado; nada será enviado ao Asaas.
            </Typography>
          </Grid>
        )}
        {preview?.transition?.transitionRequired &&
          preview?.transition?.strategy !== "USE_MONTHLY_CHANGE_R3" && (
            <Grid item xs={12}>
              <Typography color="textSecondary">
                Esta alteração muda o ciclo financeiro. Ela será agendada para
                {` ${preview.transition.effectiveDate || "o fim do período atual"}`};
                a compra já aprovada não será alterada e nenhuma assinatura
                será cancelada automaticamente.
              </Typography>
            </Grid>
          )}

        <Grid item xs={12} md={3}>
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel>Plano base</InputLabel>
            <Select
              value={terms.basePlanId}
              onChange={change("basePlanId")}
              label="Plano base"
            >
              {plans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.catalogName || plan.name} (v{plan.version || 1})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            label="Valor contratado"
            value={terms.amount}
            onChange={change("amount")}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel>Recorrência</InputLabel>
            <Select
              value={terms.recurrence}
              onChange={change("recurrence")}
              label="Recorrência"
            >
              <MenuItem value="monthly">Mensal</MenuItem>
              <MenuItem value="quarterly">Trimestral</MenuItem>
              <MenuItem value="semiannual">Semestral</MenuItem>
              <MenuItem value="yearly">Anual</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            type="date"
            label="Vencimento"
            InputLabelProps={{ shrink: true }}
            value={terms.dueDate}
            onChange={change("dueDate")}
          />
        </Grid>
        {["users", "connections", "queues", "aiTokensLimit"].map((field) => (
          <Grid item xs={6} md={2} key={field}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              type="number"
              label={
                field === "users"
                  ? "Usuários"
                  : field === "connections"
                  ? "Conexões"
                  : field === "queues"
                  ? "Filas"
                  : "Tokens IA"
              }
              value={terms[field]}
              onChange={change(field)}
            />
          </Grid>
        ))}
        <Grid item xs={12} md={4}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            label="Motivo da alteração desta empresa (auditoria)"
            helperText="Somente esta empresa será alterada; os demais contratos permanecem iguais."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Grid>
        {monthlyFlow && (
          <Grid item xs={12} md={4}>
            <FormControl variant="outlined" fullWidth size="small">
              <InputLabel>Vigência da mudança mensal</InputLabel>
              <Select
                value={monthlyPolicy}
                onChange={(event) => {
                  setMonthlyPolicy(event.target.value);
                  setPreview(null);
                  setIdempotencyKey("");
                }}
                label="Vigência da mudança mensal"
              >
                <MenuItem value="FUTURE_ONLY">
                  Somente cobranças futuras ainda não geradas
                </MenuItem>
                <MenuItem value="PENDING_AND_FUTURE">
                  Pendentes mostradas na prévia e futuras
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            disabled={
              loading ||
              requiresInitialization ||
              (monthlyFlow && (!featureEnabled || !monthlyFeatureEnabled))
            }
            onClick={handlePreview}
          >
            Gerar prévia
          </Button>
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={
              !featureEnabled ||
              requiresInitialization ||
              (monthlyFlow && !monthlyFeatureEnabled) ||
              !preview ||
              loading
            }
            onClick={handleApply}
          >
            {monthlyFlow ? "Aplicar mudança mensal" : "Aplicar somente nesta"}
          </Button>
        </Grid>

        {preview && (
          <Grid item xs={12}>
            <Typography>
              Afetadas: {privatePreview.affectedCompanyCount}. Preservadas no
              plano atual: {privatePreview.otherCompaniesOnCurrentPlan}. Campos
              alterados:{" "}
              {(privatePreview.changedFields || []).join(", ") || "nenhum"}.
            </Typography>
            {preview.providerPreview && (
              <>
                <Typography>
                  Assinatura preservada:{" "}
                  {preview.providerPreview.subscription.id}. Cobranças já
                  geradas: {preview.providerPreview.payments.length}. Pendentes
                  que serão alteradas:{" "}
                  {preview.providerPreview.affectedPayments.length}. Faturas
                  locais exatas: {preview.providerPreview.invoiceChanges.length}.
                </Typography>
                {(preview.providerPreview.payments || []).map((payment) => (
                  <Typography key={payment.id} color="textSecondary">
                    {payment.id} — {payment.status} — R${" "}
                    {(Number(payment.valueCents) / 100).toFixed(2)} —{" "}
                    {payment.dueDate || "sem vencimento"}
                  </Typography>
                ))}
              </>
            )}
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default CompanyBillingContractPanel;
